// UC21 — SYSTEM_ADMIN Registrar enrollment list ("Entități raportoare").
// per docs/shape-uc-21-registrator-entity-file.md §6 UI shape and the wireframe
// wireframes/react/src/pages/AdminRegistratori.tsx — the row opens the entity
// file; entity status, RSUD status and the machine-credential state read on one
// line, because those three decide whether a Registrar can report.
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudIcon, MudSelect, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  EmptyRegisterMessage,
  FilterBar,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
  type ActiveFilter,
} from '@simsm/ui-shared';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { CredentialTag } from '../../features/registrars/CredentialTag';
import { DeactivationEffects } from '../../features/registrars/DeactivationEffects';
import {
  listRegistrars,
  refreshFromRsud,
  rsudStatutLabel,
  type RegistrarRow,
} from '../../features/registrars/api';
import { REGISTRAR_KINDS } from '@simsm/shared-types';
import './registrars.css';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}

// The RSUD lifecycle values MConnect has actually sent. Not an enum SIMSM owns:
// these are the `statut` strings of the company-changed events.
const RSUD_STATUTES = ['ACTIV', 'SUSPENDAT', 'RADIAT'] as const;

// The criteria live in the address (wireframe §7.2 c.12–13), so a filtered
// register can be linked to, bookmarked and reloaded.
const FILTER_KEYS = ['q', 'tip', 'statut', 'rsud', 'sistem'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

// The sentinel the resynchronising state uses for "the whole register", which
// no IDNO can collide with.
const ALL = 'ALL';

export function RegistrarsPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [searchParams, setSearchParams] = useCriteriaParams();
  // `null` = idle, an IDNO = that row is resynchronising, `ALL` = the header
  // action is walking the register.
  const [resyncing, setResyncing] = useState<string | null>(null);

  const criteria = useMemo(
    () => ({
      q: searchParams.get('q') ?? '',
      tip: searchParams.get('tip') ?? '',
      statut: searchParams.get('statut') ?? '',
      rsud: searchParams.get('rsud') ?? '',
      sistem: searchParams.get('sistem') ?? '',
    }),
    [searchParams],
  );

  function setCriterion(key: FilterKey, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  function resetCriteria(): void {
    const next = new URLSearchParams(searchParams);
    for (const key of FILTER_KEYS) next.delete(key);
    setSearchParams(next);
  }

  const registrars = useQuery({
    queryKey: ['admin-registrars', criteria],
    queryFn: () =>
      listRegistrars({
        q: criteria.q,
        kind: criteria.tip,
        ...(criteria.statut === 'activ'
          ? { active: true }
          : criteria.statut === 'inactiv'
            ? { active: false }
            : {}),
        rsud: criteria.rsud,
        system:
          criteria.sistem === 'configurat'
            ? 'configured'
            : criteria.sistem === 'neconfigurat'
              ? 'unconfigured'
              : '',
      }),
  });

  // The unfiltered count, so the footer can say "5 din 20" the way the wireframe
  // does. It is a separate read rather than a second number on the same
  // response: the filtered call already narrows server-side, and asking for the
  // total is one query the cache holds for every criteria combination.
  const allRegistrars = useQuery({
    queryKey: ['admin-registrars', 'total'],
    queryFn: () => listRegistrars({}),
  });

  const update = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiClient.patch(`/admin/registrars/${id}`, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-registrars'] }),
  });

  const rows = registrars.data ?? [];
  const totalCount = allRegistrars.data?.length ?? rows.length;

  // caiet UC21-07: the register asks the entity file's question, with the same effects
  // (TC-UC21-07-01), per docs/shape-ops-registrar-kind-change-confirmation.md §2 D3.
  async function toggleActive(row: RegistrarRow): Promise<void> {
    if (row.active) {
      const ok = await confirm({
        title: t('registrar_detail.confirm.title'),
        body: (
          <DeactivationEffects
            name={row.name}
            credentialActive={row.systemCredential.configured && row.systemCredential.active}
            siteCount={row.sites.total}
          />
        ),
        confirmLabel: t('registrar_detail.confirm.action'),
        destructive: true,
      });
      if (!ok) return;
    }
    update.mutate({ id: row.id, active: !row.active });
  }

  // caiet UC21-04. One Registrar at a time: the wireframe's header button
  // resynchronises the whole register, but a per-entity call is what the audit
  // trail can attribute, and the header action below walks the rows through the
  // same path rather than opening a second one.
  async function resync(row: RegistrarRow): Promise<void> {
    setResyncing(row.idno);
    try {
      const result = await refreshFromRsud(row.idno);
      await queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
      toast({
        variant: result.applied ? 'success' : 'info',
        title: result.applied
          ? t('registrars.rsud.resynced.title')
          : t('registrars.rsud.unchanged.title'),
        text: result.applied
          ? t('registrars.rsud.resynced.body', {
              name: result.name,
              statut: rsudStatutLabel(t, result.statut),
            })
          : t('registrars.rsud.unchanged.body', { name: row.name }),
        closeLabel: t('common.close'),
      });
    } catch (err) {
      toast({
        variant: 'error',
        title: t('registrars.rsud.failed.title'),
        text: t(apiErrorKey(err, 'registrars.rsud.failed.body')),
        closeLabel: t('common.close'),
      });
    } finally {
      setResyncing(null);
    }
  }

  // caiet UC21-04 at register scope — the wireframe's header action
  // (AdminRegistratori.tsx:250-266). One MConnect read per Registrar through
  // the same endpoint the row action uses, so there is one write path and one
  // audit trail; the summary says what happened rather than "done".
  async function resyncAll(): Promise<void> {
    setResyncing(ALL);
    let applied = 0;
    let unchanged = 0;
    let failed = 0;
    for (const row of rows) {
      try {
        const result = await refreshFromRsud(row.idno);
        if (result.applied) applied += 1;
        else unchanged += 1;
      } catch {
        failed += 1;
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
    setResyncing(null);
    toast({
      variant: failed > 0 ? 'warning' : 'success',
      title: t('registrars.rsud.bulk.title'),
      text: t('registrars.rsud.bulk.body', { applied, unchanged, failed }),
      closeLabel: t('common.close'),
    });
  }

  // The wireframe's own order (AdminRegistratori.tsx:392-399): the row-actions
  // track LEADS, then Registrar, Statut entitate, Statut RSUD, Sistem
  // Registrator, Cod (MPass), Locații. The 56px track is the sites register's,
  // which docs/ui-shape.md §Shared leading row-actions column names as the
  // reference table.
  const columns: TableColumn[] = [
    { key: 'actions', label: t('registrars.column.actions'), width: '56px' },
    { key: 'name', label: t('registrars.column.name'), width: '28%' },
    // Owner ruling Q2, 2026-09-11 (spec 052 US7): the type is a column of its
    // own. A recorded divergence from AdminRegistratori.tsx:452-453, which
    // writes it on the line under the name.
    { key: 'kind', label: t('registrars.column.kind') },
    { key: 'status', label: t('registrars.column.status') },
    { key: 'rsud', label: t('registrars.column.rsud') },
    { key: 'system', label: t('registrars.column.system') },
    // Owner ruling, 2026-09-11: the IDNO is a column of its own, beside the code
    // it is so often the prefix of. Another recorded divergence from
    // AdminRegistratori.tsx:449-455, which writes it on the line under the name;
    // UC21-01 lists it among what the register shows and filters by.
    { key: 'idno', label: t('registrars.column.idno') },
    { key: 'mpassOrgCode', label: t('registrars.column.mpass_code') },
    { key: 'sites', label: t('registrars.column.sites'), align: 'end' },
  ];

  const activeFilters: ActiveFilter[] = [
    ...(criteria.statut
      ? [
          {
            key: 'statut',
            label: t('registrars.filter.status'),
            value:
              criteria.statut === 'activ'
                ? t('registrars.status.active')
                : t('registrars.status.inactive'),
            onRemove: () => setCriterion('statut', ''),
          },
        ]
      : []),
    ...(criteria.tip
      ? [
          {
            key: 'tip',
            label: t('registrars.filter.kind'),
            value: t(`registrars.kind.${criteria.tip}`),
            onRemove: () => setCriterion('tip', ''),
          },
        ]
      : []),
    ...(criteria.rsud
      ? [
          {
            key: 'rsud',
            label: t('registrars.filter.rsud'),
            value: rsudStatutLabel(t, criteria.rsud),
            onRemove: () => setCriterion('rsud', ''),
          },
        ]
      : []),
    ...(criteria.sistem
      ? [
          {
            key: 'sistem',
            label: t('registrars.filter.system'),
            value:
              criteria.sistem === 'configurat'
                ? t('registrars.filter.system_configured')
                : t('registrars.filter.system_unconfigured'),
            onRemove: () => setCriterion('sistem', ''),
          },
        ]
      : []),
  ];

  // An empty register keeps its table: the wireframe leaves the head, the frame
  // and the row counter standing and puts the message in one centred cell
  // spanning every column (AdminRegistratori.tsx:494). Both QueryState branches
  // render this same node; mud-table draws its `empty` slot once `rows` is [].
  const register = (
    <>
      {/* Seven columns in the wireframe's seven positions
        (AdminRegistratori.tsx:392-399): the row-actions track leads, and the
        first two data columns are headed "Registrator" and "Statut entitate".
        Until specs/037-admin-registers the actions trailed and those two
        headings read "Denumire" and "Stare" (parity A-10).

        Every cell is slotted, because the register reads on one line whether a
        Registrar can report and that takes a tag, a link and a menu, not a
        string; and an inactivated row mutes its text the way the wireframe's
        `tr.inactiv` does.

        Width: at the register density this table asks for 985px inside a
        1006px frame, so nothing scrolls sideways and nothing is cut
        (docs/ui-shape.md §Register table width correction). The claim that
        it scrolls "past six columns" was inherited from the geography
        correction and was never measured here. */}
      <div className="simsm-registrars__table">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={rows.map((r) => ({ id: r.id }))}
          rowStyle="zebra"
          aria-label={t('registrars.title')}
          // The whole row opens the file (AdminRegistratori.tsx:407 `tr.clickable`).
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const r = rows[index];
            if (r) navigate(fileOf(r));
          })}
        >
          <span slot="header-cell-actions" className="visually-hidden">
            {t('registrars.column.actions')}
          </span>
          {/* Nine columns since the IDNO got its own: the headers may run to two
              lines, as the wireframe's `.tbl.compact th` allows so a register
              fits beside the expanded sidebar. The name is the column that
              gives way otherwise, and a name broken over four lines is worse
              than „Statut / entitate" over two. */}
          {columns
            .filter((column) => column.key !== 'actions')
            .map((column) => (
              <span
                key={`header-${column.key}`}
                slot={`header-cell-${column.key}`}
                className="simsm-table__th"
              >
                {column.label}
              </span>
            ))}
          <EmptyRegisterMessage>
            {activeFilters.length > 0 ? t('registrars.empty_filtered') : t('registrars.empty')}
          </EmptyRegisterMessage>
          {rows.map((r, index) => {
            const cell = `simsm-registrars__cell${r.active ? '' : ' simsm-registrars__cell--inactive'}`;
            return (
              <Fragment key={r.id}>
                <span slot={`cell-name-${index}`} className={`${cell} simsm-registrars__cell-name`}>
                  {/* Plain text, not a link: the whole row opens the file. */}
                  <span className="simsm-registrars__name">{r.name}</span>
                </span>
                {/* Never blank: an unset type is the state UC21-02 forbids and the
                  reason a Registrar is locked out of the IMSP functions, so
                  the register says so. */}
                <span slot={`cell-kind-${index}`} className={cell}>
                  {r.kind ? t(`registrars.kind.${r.kind}`) : t('registrars.kind_unset')}
                </span>
                <span slot={`cell-status-${index}`} className={cell}>
                  <MudTag
                    variant="status"
                    size="sm"
                    semantic={r.active ? 'success' : 'neutral'}
                    label={
                      r.active ? t('registrars.status.active') : t('registrars.status.inactive')
                    }
                  />
                </span>
                {/* Every RSUD state is a pill, including the one MConnect has
                  never answered for. The wireframe draws three
                  (AdminRegistratori.tsx:459-470) and none of them is text; a
                  fourth state rendered as words made the whole column read as
                  unset on a register where nothing had synchronised yet. */}
                <span slot={`cell-rsud-${index}`} className={cell}>
                  <MudTag
                    variant="status"
                    size="sm"
                    semantic={rsudSemantic(r.rsud.statut)}
                    label={
                      r.rsud.statut ? rsudStatutLabel(t, r.rsud.statut) : t('registrars.rsud.never')
                    }
                  />
                </span>
                <span slot={`cell-system-${index}`} className={cell}>
                  {/* The wireframe makes this cell a link to where the credential
                    is actually administered (AdminRegistratori.tsx:472-481), so
                    the register is a way in rather than a dead end. Its
                    `.tag-link` holds the chevron back until hover or focus, so
                    the column reads as states rather than as links; that half
                    lives in registrars.css. */}
                  <RouterLink
                    className="simsm-registrars__system-link"
                    to={`/system-admin/credentials?registrar=${r.idno}`}
                    size="sm"
                    variant="strict"
                    underline="none"
                    aria-label={t('registrars.system.open_aria', { name: r.name })}
                  >
                    <CredentialTag
                      configured={r.systemCredential.configured}
                      active={r.systemCredential.active}
                    />
                    <MudIcon slot="icon-end" name="chevron-right" size={16} />
                  </RouterLink>
                </span>
                <span slot={`cell-idno-${index}`} className={cell}>
                  <span className="simsm-table__keep">{r.idno}</span>
                </span>
                <span slot={`cell-mpassOrgCode-${index}`} className={cell}>
                  {/* Body text, as the wireframe's `td.num` draws it: the code is an
                    identifier the administrator reads, not source code. */}
                  {r.mpassOrgCode ? (
                    <span className="simsm-table__keep">{r.mpassOrgCode}</span>
                  ) : (
                    <span className="simsm-registrars__muted">
                      {t('registrars.mpass_code.unset')}
                    </span>
                  )}
                </span>
                <span
                  slot={`cell-sites-${index}`}
                  className={`${cell} simsm-registrars__cell-count`}
                >
                  {r.sites.total > 0 ? (
                    r.sites.total
                  ) : (
                    <span className="simsm-registrars__muted">—</span>
                  )}
                </span>
                <span
                  slot={`cell-actions-${index}`}
                  className={`${cell} simsm-registrars__cell--actions`}
                >
                  <RowActionsMenu
                    label={t('registrars.column.actions')}
                    actions={[
                      {
                        label: t('registrars.action.open'),
                        onSelect: () => navigate(fileOf(r)),
                      },
                      {
                        label: t('registrars.action.edit'),
                        onSelect: () => navigate(`/system-admin/registrars/${r.id}/edit`),
                      },
                      {
                        label:
                          resyncing === r.idno
                            ? t('registrars.action.rsud_resync_pending')
                            : t('registrars.action.rsud_resync'),
                        disabled: resyncing !== null,
                        onSelect: () => void resync(r),
                      },
                      {
                        label: t('registrars.action.sites'),
                        onSelect: () => navigate(`/system-admin/sites?registrar=${r.idno}`),
                      },
                      {
                        label: t('registrars.action.credentials'),
                        onSelect: () => navigate(`/system-admin/credentials?registrar=${r.idno}`),
                      },
                      {
                        label: t('registrars.action.history'),
                        onSelect: () => navigate(`${fileOf(r)}?tab=history`),
                      },
                      {
                        label: r.active
                          ? t('registrars.action.deactivate')
                          : t('registrars.action.activate'),
                        danger: r.active,
                        disabled: update.isPending,
                        separator: true,
                        onSelect: () => void toggleActive(r),
                      },
                    ]}
                  />
                </span>
              </Fragment>
            );
          })}
        </MudTable>
      </div>
      <p className="simsm-registrars__footer">
        {rows.length === totalCount
          ? t('registrars.footer.count', { count: rows.length })
          : t('registrars.footer.count_of', { count: rows.length, total: totalCount })}
      </p>
    </>
  );

  return (
    <section className="simsm-page simsm-registrars">
      <PageHeader
        title={t('registrars.title')}
        lede={t('registrars.lede')}
        actions={
          <>
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              disabled={resyncing !== null || rows.length === 0}
              onClick={() => void resyncAll()}
            >
              <MudIcon slot="icon-start" name="rotate-arrow" size={20} />
              {resyncing === ALL
                ? t('registrars.rsud.resync_all_pending')
                : t('registrars.rsud.resync_all')}
            </MudButton>
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => navigate('/system-admin/registrars/new')}
            >
              <MudIcon slot="icon-start" name="plus-large" size={20} />
              {t('registrars.action.new')}
            </MudButton>
          </>
        }
      />

      <FilterBar
        search={{
          value: criteria.q,
          onChange: (value) => setCriterion('q', value),
          placeholder: t('registrars.search.placeholder'),
          ariaLabel: t('registrars.search.aria_label'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={resetCriteria}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('registrars.filter.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('registrars.filter.status')}</span>
              <MudSelect
                size="medium"
                value={criteria.statut}
                aria-label={t('registrars.filter.status')}
                placeholder={t('registrars.filter.all')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setCriterion('statut', e.detail.value)
                }
              >
                <option value="">{t('registrars.filter.all')}</option>
                <option value="activ">{t('registrars.status.active')}</option>
                <option value="inactiv">{t('registrars.status.inactive')}</option>
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('registrars.filter.kind')}</span>
              <MudSelect
                size="medium"
                value={criteria.tip}
                aria-label={t('registrars.filter.kind')}
                placeholder={t('registrars.filter.all_kinds')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setCriterion('tip', e.detail.value)
                }
              >
                <option value="">{t('registrars.filter.all_kinds')}</option>
                {REGISTRAR_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`registrars.kind.${kind}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('registrars.filter.rsud')}</span>
              <MudSelect
                size="medium"
                value={criteria.rsud}
                aria-label={t('registrars.filter.rsud')}
                placeholder={t('registrars.filter.all')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setCriterion('rsud', e.detail.value)
                }
              >
                <option value="">{t('registrars.filter.all')}</option>
                {RSUD_STATUTES.map((statut) => (
                  <option key={statut} value={statut}>
                    {t(`registrars.rsud.statut.${statut}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('registrars.filter.system')}</span>
              <MudSelect
                size="medium"
                value={criteria.sistem}
                aria-label={t('registrars.filter.system')}
                placeholder={t('registrars.filter.all')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setCriterion('sistem', e.detail.value)
                }
              >
                <option value="">{t('registrars.filter.all')}</option>
                <option value="configurat">{t('registrars.filter.system_configured')}</option>
                <option value="neconfigurat">{t('registrars.filter.system_unconfigured')}</option>
              </MudSelect>
            </label>
          </>
        }
      />

      <QueryState query={registrars} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>
    </section>
  );
}

// SUSPENDAT is a warning, RADIAT is the end of the legal entity; rendering both
// as "accent" made a struck-off Registrar look like a temporary state.
//
// The wireframe paints exactly three (AdminRegistratori.tsx:459-470): activ →
// success, suspendat → its own `accent`, which is brand blue and is what AGE
// calls `brand`, radiat → danger. Two more states reach this column and the
// wireframe draws neither: `INACTIV`, which MConnect's fake emits and a real
// feed may too, and `null`, meaning no company-changed event has ever arrived.
// Both are "no signal", not a notice, so both take a semantic that carries no
// alarm — `neutral` for a dormant entity, `muted` for an absence of data —
// rather than borrowing the blue that means "look at this".
function rsudSemantic(statut: string | null): 'success' | 'brand' | 'danger' | 'neutral' | 'muted' {
  if (statut === null) return 'muted';
  if (statut === 'ACTIV') return 'success';
  if (statut === 'RADIAT') return 'danger';
  if (statut === 'SUSPENDAT') return 'brand';
  return 'neutral';
}

function fileOf(row: RegistrarRow): string {
  return `/system-admin/registrars/${row.idno}`;
}
